// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.D3TreemapLabels.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { d3 } from '../Izenda.D3Wrapper';
import { isString, map, trim, isEmpty, last } from 'lodash/fp';
import { hideOverlappingLabels, getContrastTextColor, calculateBoxByRotateDegree } from '../Izenda.D3DrawUtils';
import { LABEL_BASELINE_SHIFT, TREEMAP_BANNER_HEIGHT } from 'logic/data/Izenda.D3VizEngineConstants';
import { isLeafNode } from '../Izenda.D3DrawUtils';
import tinycolor from 'tinycolor2';

// Stroke width of outline
const OUTLINE_WIDTH = 2;
// Banner padding
const BANNER_PADDING = 2;

/**
 * Draw chart labels
 * @param {*} labelGraphic
 * @param {*} labelsInfo
 * @param {*} options
 */
export function drawTreemapLabels(
  labelGraphic: d3.Selection<SVGGraphicsElement>,
  labelsInfo,
  options: Object,
  plotWidth: Number,
  plotHeight: Number,
  treeHeight: Number
) {
  const {
    izendaOptions: { showSliceLabel: izSliceLabel, showValueLabel: izValueLabel }
  } = options;

  if ((izSliceLabel || izValueLabel) && labelGraphic.attr('visibility') !== 'hidden') {
    const labels = drawLabels(labelGraphic, labelsInfo, plotWidth, plotHeight, options, treeHeight);

    hideOverlappingLabels(labels.nodes());

    return labels;
  }

  return d3.selectAll(null);
}

/**
 * Draw labels
 * @param {d3.Selection<SVGGElement>} host
 * @param {Array<Object>} labelsInfo
 * @param {Number} plotWidth
 * @param {Number} plotHeight
 * @param {Object} options
 */
function drawLabels(
  host: d3.Selection<SVGGElement>,
  labelsInfo: Array,
  plotWidth: Number,
  plotHeight: Number,
  options: Object,
  treeHeight: Number
) {
  const {
    izendaOptions: { showValueLabel: izValueLabel },
    commonActions: { getDataLabelFormatProcess, getValueLabel },
    izendaSeriesConfig
  } = options;

  return host
    .selectAll('text')
    .data(labelsInfo)
    .enter()
    .append('text')
    .attr('x', ({ label: { x, width }, node }) => (isLeafNode(node, treeHeight) ? x + width / 2 : x + BANNER_PADDING))
    .attr('y', ({ label: { y, height }, node }) => (isLeafNode(node, treeHeight) ? y + height / 2 : y))
    .classed('u_textLink', ({ point, serie }) => isTextLink(point.labelName || serie.labelName))
    .text(d => trim(getLabelContent(d)))
    .style('text-anchor', ({ node }) => (isLeafNode(node, treeHeight) ? 'middle' : 'start'))
    .style('font-weight', 'bold')
    .attr('fill', ({ point, node }) => (isLeafNode(node, treeHeight) ? getContrastTextColor(point.color) : '#FFF'))
    .attr('transform', ({ node, label: { rotation, width, height, x, y } }) =>
      isLeafNode(node, treeHeight) ? `rotate(${rotation || 0}, ${x + width / 2}, ${y + height / 2})` : ''
    )
    .classed('d3-no-rotate', ({ node }) => isLeafNode(node, treeHeight))
    .call(textElements => textElements.each(wrapLabelText))
    .attr('visibility', getTextVisibility);

  /**
   * Check if label should be treat as text link
   * @param {String} labelName
   * @returns {Boolean}
   */
  function isTextLink(labelName: String) {
    const { customURL, subReport, embeddedJavascript } = izendaSeriesConfig[labelName];
    return customURL || subReport || embeddedJavascript;
  }

  /**
   * Get label content
   * @param {Object} param0
   */
  function getLabelContent({ point, serie }) {
    const pointParam = { ...point, options: point };
    const serieParam = { userOptions: serie };
    return getDataLabelFormatProcess(pointParam, serieParam, '', false, true);
  }

  /**
   * Add nested normal style tspan inside a tspan
   * @param {d3.Selection<SVGTSpanElement>} tspan
   * @param {String} word
   */
  function addNestedNormalStyleTspan(tspan: d3.Selection<SVGTSpanElement>, word: String) {
    tspan
      .append('tspan')
      .text(word)
      .style('font-weight', 'normal');
  }

  /**
   * Create tspan element inside text element
   * @param {d3.Selection<SVGTextElement>} text
   * @param {String} word
   */
  function createTspan(text: d3.Selection<SVGTextElement>, word: String) {
    return text
      .append('tspan')
      .attr('x', text.attr('x'))
      .attr('y', text.attr('y'))
      .attr('dy', `${text.node().childNodes.length - 1}em`)
      .text(word);
  }

  // Wrap label text and break to new lines when exceed the width
  function wrapLabelText() {
    const text = d3.select(this);
    const {
      point,
      serie,
      node,
      label: { width, height }
    } = text.datum();
    const valueLabel = getValueLabel(point, serie, point.name, point.originalNameValue);
    const valueLabelLength = isString(valueLabel) ? trim(valueLabel).split(/\s+/).length : 1;
    const isLeaf = isLeafNode(node, treeHeight);

    // Split by space
    const words = text.text().split(/\s+/);
    let line = [];
    let lineValueLabel = [];
    let tspan = text
      .text(null)
      .append('tspan')
      .attr('x', text.attr('x'))
      .attr('y', text.attr('y'));

    words.forEach((word, index) => {
      // If value label is show, last word is always the value label
      const isValueLabelWord = izValueLabel && index >= words.length - valueLabelLength;
      if (!isValueLabelWord) {
        line.push(word);
        tspan.text(line.join(' '));
      } else {
        // Add tspan instead of string if word is value label and do not bold it
        lineValueLabel.push(word);
        if (line.length > 0) {
          tspan.text(`${line.join(' ')} `);
          addNestedNormalStyleTspan(tspan, lineValueLabel.join(' '));
        } else {
          tspan.text(`${lineValueLabel.join(' ')}`).style('font-weight', 'normal');
        }
      }

      const { width: tspanWidth, height: tspanHeight } = tspan.node().getBoundingClientRect();

      // If text in tspan is not banner and longer than width slice then split into new tspan
      if ((tspanWidth > width || tspanHeight > height) && isLeaf) {
        !isValueLabelWord ? line.pop() : lineValueLabel.pop();

        if (line.length > 0) {
          tspan.text(line.join(' '));
          if (lineValueLabel.length > 0) {
            addNestedNormalStyleTspan(tspan, ` ${lineValueLabel.join(' ')}`);
          }
          tspan = createTspan(text, word);

          if (isValueLabelWord) {
            tspan.style('font-weight', 'normal');
          }
        } else {
          if (lineValueLabel.length > 0) {
            tspan.text(lineValueLabel.join(' '));
            tspan = createTspan(text, word);
            tspan.style('font-weight', 'normal');
          }
        }

        if (!isValueLabelWord) {
          line = [word];
        } else {
          line = [];
          lineValueLabel = [word];
        }
      }
    });

    // Reposition text
    repositionText(text, height, treeHeight);

    // Wrap banner text
    !isLeaf && wrapBannerText(text, width);

    // Add outline
    addTextOutline(text);
  }

  // Get visibility of the text
  function getTextVisibility() {
    const text = d3.select(this);
    const {
      node,
      label: { height, rotation }
    } = text.datum();

    if (!isLeafNode(node, treeHeight)) {
      // Using tspan bbox height to avoid IE return wrong height when using text bbox height
      const tspan = text.select('tspan');
      const tspanHeight = !tspan.empty() ? tspan.node().getBoundingClientRect().height : 0;
      return tspanHeight > Math.min(height, TREEMAP_BANNER_HEIGHT) ? 'hidden' : 'visible';
    }

    const { x: textX, y: textY, width: textWidth, height: textHeight } = !rotation
      ? this.getBBox()
      : calculateBoxByRotateDegree(this.getBBox(), rotation);
    // if label is outside of treemap then hide
    return textHeight > height ||
      textX < 0 ||
      textY < 0 ||
      textX + textWidth > plotWidth ||
      textY + textHeight > plotHeight
      ? 'hidden'
      : 'visible';
  }
}

/**
 * Add text outline
 * @param {d3.Selection<SVGTextElement>} text
 */
function addTextOutline(text: d3.Selection<SVGTextElement>) {
  // We using map function here to create a new separate list
  // so cloning tspan do not mess up text child node list iterator
  text
    .selectAll(() => map(node => node, text.node().childNodes))
    .each(function() {
      const tspanSelection = d3.select(this);
      const strokeColor = tinycolor(text.attr('fill')).isLight() ? '#000' : '#FFF';
      tspanSelection.clone(true);
      tspanSelection
        .attr('stroke', strokeColor)
        .attr('stroke-width', OUTLINE_WIDTH)
        .attr('stroke-linejoin', 'round')
        .classed('d3-labels-outline', true);
    });
}

/**
 * Reposition text element after render all the line
 * @param {d3.Selection<SVGTextElement>} text
 * @param {Number} height
 * @param {Number} treeHeight
 */
function repositionText(text: d3.Selection<SVGTextElement>, height: Number, treeHeight: Number) {
  const data = text.datum();
  // Recalculate y position
  const finalTextY = !isLeafNode(data.node, treeHeight)
    ? data.label.y + BANNER_PADDING
    : data.label.y + (height - text.node().getBBox().height) / 2;
  // Reposition text
  text
    .attr('y', finalTextY)
    .selectAll(() => text.node().childNodes)
    .attr('y', finalTextY)
    .attr('dy', function() {
      const currentDy = parseFloat(d3.select(this).attr('dy') || 0);
      return `${currentDy + LABEL_BASELINE_SHIFT}em`;
    });
}

/**
 * Wrap banner text
 * @param {d3.Selection<SVGTextElement>} text
 * @param {Number} width
 */
function wrapBannerText(text: d3.Selection<SVGTextElement>, width: Number) {
  // Current text width
  let currentWidth = text.node().getBBox().width;
  // The final tspan inside this text element, which will be truncated first
  let lastTspan = d3.select(last(text.selectAll('tspan').nodes()));
  // The last tspan text
  let lastTspanText = lastTspan.text();

  while (currentWidth + BANNER_PADDING > width - BANNER_PADDING && !isEmpty(text.text())) {
    // If all the text in last tspan is truncated
    if (isEmpty(lastTspanText)) {
      // Remove it and select new last tspan
      lastTspan.remove();
      lastTspan = d3.select(last(text.selectAll('tspan').nodes()));
      if (lastTspan.empty()) return;
      lastTspanText = lastTspan.text();
    }
    lastTspanText = lastTspanText.slice(0, -1);
    lastTspan.text(`${lastTspanText}…`);
    currentWidth = text.node().getBBox().width;
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/d3/treemap/Izenda.D3TreemapLabels.js